import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowData,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "src/components/base/table";
import { LinkProps } from "src/components/base/link";
import clsx from "clsx";
import {
    Pagination,
    PaginationGap,
    PaginationList,
    PaginationNext,
    PaginationPage,
    PaginationPrevious,
} from "src/components/base/pagination";

declare module "@tanstack/react-table" {
    /** Augments TanStack Table's per-column `meta` field */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- augmenting ColumnMeta requires matching its type parameters
    interface ColumnMeta<TData extends RowData, TValue> {
        /** Extra classes applied to every {@link TableCell} of this column */
        className?: string;
    }
}

/**
 * Returns page numbers (1-based) with "gap" sentinels for ellipsis positions.
 *
 * @param current the current page number
 * @param total total page number
 * @returns an array of page numbers or "gap" sentinels
 */
function buildPageNumbers(current: number, total: number): (number | "gap")[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: (number | "gap")[] = [1];
    if (current > 3) pages.push("gap");
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
        pages.push(p);
    }
    if (current < total - 2) pages.push("gap");
    pages.push(total);
    return pages;
}

/**
 * Sets up a TanStack Table instance with sorting, filtering, and pagination.
 *
 * ## Leaf rows vs. group rows
 *
 * TanStack Table has two kinds of rows:
 * - **Leaf rows** - the actual data rows, one per item in `data`. Every row you
 *   see in the rendered table is (or should be) a leaf row.
 * - **Group rows** - synthetic aggregated rows that TanStack creates when its
 *   built-in grouping model (`getGroupedRowModel`) is active. They appear in
 *   `getRowModel().rows` alongside the leaf rows, which causes each item to be
 *   rendered twice — once as the group row and once as its child leaf row.
 *
 * This hook deliberately does **not** use `getGroupedRowModel`, so every row
 * returned by the table is always a leaf row. Visual grouping (the separator
 * line between tenants) is achieved purely through sorting: when `isGrouped` is
 * true the groupBy column is prepended to the active sort, keeping same-group
 * rows consecutive. `DataTable` then detects the value change between adjacent
 * rows and draws a divider - no synthetic rows, no duplicates, normal
 * per-item pagination.
 */
export function useDataTable<T>({
    data,
    columns,
    initialSorting = [],
    groupBy = [],
    pageIndex,
    onPageChange,
    pageSize = 25,
}: {
    data: T[];
    columns: ColumnDef<T, string>[];
    initialSorting?: SortingState;
    groupBy?: string[];
    /**
     * The current page index (0-based). Pagination is fully controlled: the
     * caller owns this state, typically as a `page` URL search param, so the
     * page survives reloads and back/forward navigation.
     */
    pageIndex: number;
    /**
     * Called with the new page index (0-based) whenever the table wants to
     * change the page (pagination buttons, `table.setPageIndex(0)` resets).
     * Typically navigates to update the `page` URL search param.
     */
    onPageChange: (pageIndex: number) => void;
    pageSize?: number;
}) {
    const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
    const [isGrouped, setIsGrouped] = React.useState<boolean>(groupBy.length > 0);

    // Stable references: groupBy and initialSorting never change after mount,
    // capture them once.
    const initialSortingRef = React.useRef(initialSorting);
    const groupByRef = React.useRef(groupBy);

    // Visual-only grouping: prepend the groupBy columns to the sort so rows with
    // the same group value are always consecutive. DataTable draws a separator at
    // each value change. We deliberately avoid TanStack's grouping model because
    // it creates synthetic group rows that appear as duplicates in the table.
    // Memoized so the array reference is stable - an unstable reference would
    // trigger autoResetPageIndex on every render, causing an infinite loop.
    const effectiveSorting = React.useMemo<SortingState>(() => {
        // When the user clears the active column sort (third click on a header),
        // fall back to the initial sorting instead of raw data order.
        const base = sorting.length > 0 ? sorting : initialSortingRef.current;
        return isGrouped
            ? [
                  ...groupByRef.current
                      .filter((id) => !base.some((s) => s.id === id))
                      .map((id) => ({ id, desc: false })),
                  ...base,
              ]
            : base;
    }, [isGrouped, sorting]);

    const table = useReactTable({
        data,
        columns,
        state: { sorting: effectiveSorting, pagination: { pageIndex, pageSize } },
        onSortingChange: setSorting,
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
            onPageChange(next.pageIndex);
        },
        globalFilterFn: "includesString",
        // Disable automatic page reset on sort/filter changes — callers reset
        // explicitly (e.g. by navigating with `page: 1`) so we don't double-reset.
        autoResetPageIndex: false,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    // Ensure the page number is not out of bounds for the content, redirecting to the last page otherwise
    const pageCount = table.getPageCount();
    const lastPageIndex = Math.max(pageCount - 1, 0);
    React.useEffect(() => {
        if (pageIndex > lastPageIndex) {
            onPageChange(lastPageIndex);
        }
    }, [pageIndex, lastPageIndex]);

    const setGrouping = React.useCallback(
        (grouped: boolean) => {
            setIsGrouped(grouped);
            table.setPageIndex(0);
        },
        [table],
    );
    return { table, isGrouped, setGrouping };
}

/**
 * The properties for {@link DataTable}
 */
export type DataTableProps<Row> = {
    /** Table to render */
    table: ReturnType<typeof useReactTable<Row>>;

    /**
     * When set, a separator is drawn between consecutive rows whose value for
     * this column differs. Pass the column IDs from `useDataTable`'s `groupBy`
     * when grouping is active; omit (or pass undefined) for flat rendering.
     */
    groupBy?: string[];

    /** Url each row should link to */
    rowHref?: LinkProps["href"];
    /** Url parameters for `rowHref` */
    rowParams?: (row: Row) => LinkProps["params"];
    /** Search parameters for `rowHref` */
    rowSearch?: (row: Row) => LinkProps["search"];
};

/**
 * Renders a TanStack Table with client-side pagination.
 *
 * All rows passed through `table.getRowModel()` are expected to be leaf rows
 * (see {@link useDataTable} for why). When `groupBy` is provided, a thin
 * separator line is inserted wherever the value of that column changes between
 * two adjacent rows, producing a visual grouping effect without needing
 * synthetic group rows.
 */
export function DataTable<Row>(props: DataTableProps<Row>) {
    const { table, groupBy } = props;
    const { rows } = table.getRowModel();
    const groupCol = groupBy?.[0];
    const colSpan = table.getAllLeafColumns().length;

    const pageIndex = table.getState().pagination.pageIndex;
    const pageCount = table.getPageCount();

    return (
        <div className="flex flex-col gap-4">
            <Table>
                <TableHead className={"sticky top-0 z-50 bg-white dark:bg-zinc-900"}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <React.Fragment key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </React.Fragment>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>

                <TableBody>
                    {rows.map((row, i) => {
                        // Insert a separator when the group-column value changes between rows.
                        const isGroupBoundary =
                            groupCol !== undefined &&
                            i > 0 &&
                            row.getValue(groupCol) !== rows[i - 1]!.getValue(groupCol);

                        return (
                            <React.Fragment key={row.id}>
                                {isGroupBoundary && (
                                    <tr>
                                        <td
                                            colSpan={colSpan}
                                            className="h-px border-t-2 border-t-zinc-300 p-0 dark:border-t-white/20"
                                        />
                                    </tr>
                                )}
                                <TableRow
                                    href={props.rowHref}
                                    params={props.rowParams?.(row.original)}
                                    search={props.rowSearch?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className={clsx(
                                                cell.column.columnDef.meta?.className,
                                                "overflow-hidden text-ellipsis whitespace-nowrap",
                                            )}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>

            <Pagination>
                <PaginationPrevious disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()} />
                <PaginationList>
                    {buildPageNumbers(pageIndex + 1, pageCount).map((item, idx) =>
                        item === "gap" ? (
                            <PaginationGap key={`gap-${idx}`} />
                        ) : (
                            <PaginationPage
                                key={item}
                                current={item === pageIndex + 1}
                                onClick={() => table.setPageIndex(item - 1)}
                            >
                                {item}
                            </PaginationPage>
                        ),
                    )}
                </PaginationList>
                <PaginationNext disabled={!table.getCanNextPage()} onClick={() => table.nextPage()} />
            </Pagination>
        </div>
    );
}
